/**
 * useTrialOffer — single source of truth for the 7-day Pro trial offer.
 *
 * Returns:
 *   - eligible: true when the user can claim the trial (free tier + never used it)
 *   - loading: while we check the server
 *   - terms:   { trial_days, trial_total_after_trial, renews_at }
 *   - startTrial(): opens Stripe Checkout in the same tab
 *
 * For logged-out visitors, eligibility = true (the CTA will route them to
 * /register first, then auto-redirect into the trial flow post-signup).
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useFootballPromotion } from '@/contexts/FootballPromotionContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PENDING_TRIAL_KEY = 'pp_start_trial_after_login';

export function useTrialOffer() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { offer, loading: offerLoading } = useFootballPromotion();
  const footballOffer = offerLoading || ['free', 'paid', 'upcoming'].includes(offer?.state);
  const [eligible, setEligible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [terms, setTerms] = useState({ trial_days: 7, trial_total_after_trial: 14.97, renews_at: 9.99 });
  const [starting, setStarting] = useState(false);

  // Check server-side eligibility for logged-in users
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setEligible(true); // visitors: route through /register first
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/subscription/trial-eligibility`, { withCredentials: true });
        setEligible(!!data.eligible);
        setTerms({
          trial_days: data.trial_days || 7,
          trial_total_after_trial: data.trial_total_after_trial || 14.97,
          renews_at: data.renews_at || 9.99,
        });
      } catch (_) {
        setEligible(false);
      }
      setLoading(false);
    })();
  }, [user, authLoading]);

  // After register/login, if the user came from a trial CTA, resume the flow
  useEffect(() => {
    if (authLoading || !user) return;
    if (footballOffer) {
      try { sessionStorage.removeItem(PENDING_TRIAL_KEY); } catch (_) {}
      return;
    }
    let pending = false;
    try { pending = sessionStorage.getItem(PENDING_TRIAL_KEY) === '1'; } catch (_) {}
    if (pending && eligible && !footballOffer) {
      try { sessionStorage.removeItem(PENDING_TRIAL_KEY); } catch (_) {}
      // Defer to avoid racing the redirect cycle
      setTimeout(() => startTrial(), 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, eligible, footballOffer]);

  const startTrial = useCallback(async () => {
    if (footballOffer) { navigate('/football-month'); return; }
    if (!user) {
      // Visitor: send to register, resume after login
      try {
        window.posthog?.capture('trial_cta_clicked', { source: 'visitor' });
      } catch (_) { /* posthog stub */ }
      try { sessionStorage.setItem(PENDING_TRIAL_KEY, '1'); } catch (_) {}
      navigate('/register?intent=trial');
      return;
    }
    if (starting) return;
    setStarting(true);
    try {
      window.posthog?.capture('trial_cta_clicked', { source: 'authed' });
    } catch (_) { /* posthog stub */ }
    try {
      const { data } = await axios.post(
        `${API}/subscription/start-trial`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data?.url) {
        try {
          window.posthog?.capture('trial_checkout_redirect', { session_id: data.session_id });
        } catch (_) { /* posthog stub */ }
        window.location.href = data.url;
      }
    } catch (err) {
      try {
        window.posthog?.capture('trial_start_failed', {
          detail: err.response?.data?.detail || 'unknown',
        });
      } catch (_) { /* posthog stub */ }
      alert(err.response?.data?.detail || 'Could not start your trial. Please try again.');
      setStarting(false);
    }
  }, [user, navigate, starting, footballOffer]);

  return { eligible: eligible && !footballOffer, loading, terms, starting, startTrial };
}
